import { queryClient } from '@/Root';
import usePartsColumns from '@/components/Operational/Inventory/Parts/usePartsColumns';
import SearchPartsExtraFilters from '@/components/SearchParts/SearchPartsExtraFilters';
import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
} from '@/components/_common';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import {
  CONVERT_ITEM_TYPE,
  Inventory,
  InventoryStatus,
  LaunchItemsRequest,
  PageResultInventory,
  ProductType,
} from '@/core/domain/inventory/inventories/Inventory.types';
import { useUpdateAllInventory } from '@/core/domain/inventory/inventories/inventory.hooks';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';
import {
  ListStockItemsQuery,
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { useDebounce, useDisclosure } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Button, Col, Form, Modal, Radio, Row, Typography } from 'antd';
import { sortBy } from 'lodash';
import React, { Key, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface Props {
  inventory: Inventory;
  inventoryPageResult?: PageResultInventory;
}

const InventoryAddItemModal: React.FC<Props> = ({
  inventory,
  inventoryPageResult,
}) => {
  const [addItemModal, addItemModalAction] = useDisclosure();
  const [form] = Form.useForm<{ productType: ProductType }>();
  const productType = Form.useWatch('productType', form);
  const { locationColumn, isFetching: isFetchingColumns } = usePartsColumns();

  const [items, setItems] = useState<LaunchItemsRequest[]>([]);

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    order: 'ASC',
    itemType: StockItemType.Part,
    companiesIds: [inventory.company.id],
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    addItemModal,
    queryDebounce,
  );

  const stockItems = flatInfiniteResult(data);

  const updateInventory = useUpdateAllInventory(inventory.id);

  const handleCancel = () => {
    setItems([]);
    addItemModalAction.close();
    setQueryParams({
      order: 'ASC',
      itemType: CONVERT_ITEM_TYPE[productType],
      companiesIds: [inventory.company.id],
    });
    queryClient.removeQueries(['stockItems']);
  };

  const handleSave = async () => {
    await updateInventory.mutateAsync({
      items,
      stockItems: stockItems.filter((stockItem) =>
        items.some(
          (item) =>
            item.partId === stockItem.partId ||
            item.consumableId === stockItem.consumableId,
        ),
      ),
    });
    handleCancel();
  };

  useEffect(() => {
    form.setFieldValue('productType', inventory.productType);
  }, [addItemModal]);

  useEffect(() => {
    if (productType) {
      setQueryParams((prev) => ({
        ...prev,
        itemType: CONVERT_ITEM_TYPE[productType],
      }));
    }
  }, [productType]);

  const columns: ColumnsType<StockItem> = [
    {
      key: 'description',
      title: 'Descrição',
      dataIndex: 'description',
      width: COLUMNS_WIDTH.billToPay,
      ellipsis: true,
      render: (value) => (
        <EllipsisHoverToggle>{value || '-'}</EllipsisHoverToggle>
      ),
    },
    {
      key: 'manufacturingCode',
      title: 'Cod. Fabricante',
      dataIndex: 'manufacturingCode',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      key: 'barcode',
      title: 'Cod. Barras',
      dataIndex: 'barcode',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      key: 'sku',
      title: 'Cod. Interno',
      dataIndex: 'sku',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      key: 'companyCode',
      title: 'Cod. Empresa',
      dataIndex: 'companyCode',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      key: 'availableStock',
      title: 'Est. Disponível',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (_, record) => {
        const totalAvailableStock = record.stock?.reduce(
          (prev, curr) => prev + curr.availableStock,
          0,
        );

        return quantityFormatter(totalAvailableStock);
      },
    },
    {
      key: 'totalStock',
      title: 'Est. Total',
      dataIndex: 'totalStock',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value) => quantityFormatter(value),
    },
    {
      key: 'Manufacturer',
      title: 'Fabricante',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      dataIndex: ['manufacturer', 'name'],
      render: (value) => value || '-',
    },
    {
      key: 'group',
      title: 'Grupo',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      dataIndex: ['group', 'name'],
      render: (value) => value || '-',
    },
    ...locationColumn,
    {
      key: 'unitaryValue',
      title: 'Vl. unitário',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (_, record) => {
        const sorted = sortBy(record.prices, (a) => a.company.id);

        const matrixPrice = sorted[0];

        if (!matrixPrice) return '-';

        return formatterPrice(matrixPrice.cost);
      },
    },
  ];

  return (
    <>
      <Button
        type="primary"
        onClick={() => addItemModalAction.open()}
        disabled={
          inventory?.status === InventoryStatus.DONE ||
          inventory?.status === InventoryStatus.PROCESSING
        }
      >
        Adicionar
      </Button>

      <Modal
        title="Adicionar itens ao inventário"
        width="90%"
        open={addItemModal}
        onCancel={handleCancel}
        onOk={handleSave}
        okText="Salvar"
        confirmLoading={updateInventory.isLoading}
      >
        <AppPageHeader
          searchValue={queryParams.search}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          extraFilters={
            <SearchPartsExtraFilters
              setQueryParams={setQueryParams}
              hiddenFilterType={true}
            />
          }
        />

        <Form form={form}>
          <Row>
            <Col span={24} md={24} lg={24}>
              <Typography.Title level={5}>Pesquisar por:</Typography.Title>

              <Form.Item initialValue="PART" name="productType">
                <Radio.Group buttonStyle="solid">
                  <Radio.Button
                    value="PART"
                    disabled={inventory.productType !== ProductType.PART}
                  >
                    <FormattedMessage id="inventory.form.modal.radio.part" />
                  </Radio.Button>

                  <Radio.Button
                    value="CONSUMABLE"
                    disabled={inventory.productType !== ProductType.CONSUMABLE}
                  >
                    <FormattedMessage id="inventory.form.modal.radio.consumables" />
                  </Radio.Button>

                  <Radio.Button
                    value="ALL"
                    disabled={inventory.productType !== ProductType.ALL}
                  >
                    <FormattedMessage id="inventory.form.modal.radio.all" />
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <InfiniteTable<StockItem>
          columns={columns}
          dataSource={stockItems}
          rowKey={(row) => (!!row.partId ? row.partId : row.consumableId)}
          loading={isFetching || isFetchingColumns}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          rowSelection={{
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            onChange: (_, items) => {
              setItems([
                ...items.map((item) => {
                  const availableStock = item.stock.reduce(
                    (prev, curr) => prev + curr.availableStock,
                    0,
                  );

                  const reserved = item.stock.reduce(
                    (prev, curr) => prev + curr.reserved,
                    0,
                  );

                  return {
                    type: !!item.partId
                      ? ProductType.PART
                      : ProductType.CONSUMABLE,
                    checked: false,
                    countedQuantity:
                      reserved >= availableStock ? reserved : availableStock,
                    price: item.prices[0].cost,
                    partId: item.partId,
                    consumableId: item.consumableId,
                  };
                }),
              ]);
            },
            selectedRowKeys: items.map((item) =>
              !!item.partId ? item.partId : item.consumableId,
            ) as Key[],
            getCheckboxProps: (item) => ({
              disabled:
                inventoryPageResult?.pagedPartsIds?.includes(
                  String(item.partId),
                ) ||
                inventoryPageResult?.pagedConsumablesIds?.includes(
                  String(item.consumableId),
                ),
            }),
          }}
        />
      </Modal>
    </>
  );
};

export default InventoryAddItemModal;
